import React, { useState } from "react"
import moment from "moment"
import { Link } from "react-router-dom"
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap"
import { drawAnswerFinding, drawFeedbackCopy, drawLogsFinding, drawMatchesFinding, resultsLossConfirmLabel } from "./drawFeedbackCopy"
import "./draw.scss"

const timeFormat = "DD MMM YYYY, HH:mm:ss"
const requestedAt = startedAt => startedAt && moment(startedAt).isValid() ? moment(startedAt).format(timeFormat) : null

export default function DrawOperationFeedback({ operation, competition, luckyDrawPath }) {
  const [confirmingUnlock, setConfirmingUnlock] = useState(false)
  if (!operation?.message) return null
  const warning = ["unknown", "blocked"].includes(operation.status)
  const unknown = operation.status === "unknown"
  const copy = drawFeedbackCopy(operation.action)
  const competitionReference = competition?.competitionId || operation.competitionId
  const phase = competition?.phases?.find(item => String(item._id) === String(operation.phaseId))
  const phaseLabel = operation.phaseId ? ` · Phase #${phase?.phaseId || operation.phaseId}` : ""
  // P120: after the one-minute window CP shows what it found and lets the operator unlock without repeating anything.
  const unlockable = unknown && operation.windowEnded
  const requested = unlockable ? requestedAt(operation.startedAt) : null
  const action = operation.action && <div className="mt-1">Action: {copy.action}{phaseLabel}{requested && ` · Requested ${requested}`}</div>
  // P51 point 5: recorded results would be lost — the same action goes again only on this explicit confirmation.
  if (operation.status === "confirm") return <div role="alert" className="alert alert-warning">
    <strong className="d-block">{operation.message}</strong>
    {action}
    <div className="mt-2">
      <Button color="danger" className="mr-2" onClick={operation.confirmResultsLoss}>{resultsLossConfirmLabel(operation.action)}</Button>
      <Button color="light" onClick={operation.dismiss}>Cancel</Button>
    </div>
  </div>
  if (operation.status === "unlocked") return <div role="status" className="alert alert-info">
    <strong className="d-block">{operation.message}</strong>
    <div className="mt-1">{operation.action && `Action: ${copy.action}${phaseLabel} · `}Nothing was repeated</div>
    <p className="mt-2 mb-0">{operation.action && `${copy.unlocked} `}If the server finishes the request later, Action Logs will show it.</p>
  </div>
  const findings = operation.findings || {}
  // A later moment on the request's own day reads as a time only.
  const since = value => moment(value).isValid() ? moment(value).format(requested && moment(value).isSame(operation.startedAt, "day") ? "HH:mm:ss" : timeFormat) : "an unknown time"
  const mainDrawPath = operation.action === "generate" && operation.eventId && operation.competitionId
    ? `/events/${operation.eventId}/competitions/${operation.competitionId}/matches` : null
  const checkButton = <Button color="primary" className={unlockable ? "mr-2" : undefined} disabled={operation.checking} onClick={operation.recover}>
    {operation.checking && <Spinner size="sm" className="mr-2" aria-hidden="true" />}
    {operation.checking ? "Checking draw status…" : "Check draw status"}
  </Button>
  const unlockButton = operation.canUnlock && <Button color="dark" outline className="mr-2" disabled={operation.checking}
    onClick={() => setConfirmingUnlock(true)}>I’ve checked — unlock draw changes</Button>
  const unlock = () => { setConfirmingUnlock(false); operation.unlock() }
  return <div role={warning || operation.status === "error" ? "alert" : "status"}
    aria-busy={operation.checking || undefined}
    className={`alert alert-${warning ? "warning" : operation.status === "error" ? "danger" : operation.status === "saved" ? "success" : "info"}`}>
    <strong className="d-block">{operation.message}</strong>
    {operation.status === "saved" && <>
      {luckyDrawPath && ["initial", "redraw", "swap"].includes(operation.action)
        ? <Link to={luckyDrawPath} className="alert-link">Open Lucky Draw</Link>
        : <span>You can continue.</span>}
    </>}
    {action}
    {unlockable && <>
      <p className="mt-2 mb-2">CP checked for 1 minute and found no result. Draw changes stay disabled until you check what happened.</p>
      <dl className="draw-operation-findings">
        <div><dt>Server answer</dt><dd>{drawAnswerFinding(findings.answer)}</dd></div>
        <div><dt>Action Logs</dt><dd>{drawLogsFinding(findings.logs, since)}</dd></div>
        {operation.action === "generate" && <div><dt>Main Draw</dt><dd>{drawMatchesFinding(findings.matches)}</dd></div>}
      </dl>
      <p className="mb-2">{copy.unlock}</p>
      <div className="d-flex flex-wrap align-items-center">
        {checkButton}
        {unlockButton}
        {mainDrawPath && <Link to={mainDrawPath} className="alert-link">Open Main Draw</Link>}
        {!mainDrawPath && luckyDrawPath && <Link to={luckyDrawPath} className="alert-link">Open Lucky Draw</Link>}
      </div>
      <p className="small mt-2 mb-0">Still unsure? Contact support{competitionReference ? ` with competition ID #${competitionReference}` : ""}.</p>
    </>}
    {unknown && !unlockable && <>
      <p className="mt-2 mb-2">{copy.explanation} Click “Check draw status” to verify the result.</p>
      {!operation.checking && operation.checkResult && <p className="mb-2">
        {operation.checkResult === "unavailable"
          ? "We couldn’t check the draw status. This does not mean the draw failed."
          : "The result is still unconfirmed. Draw changes remain disabled."}{" "}
        Check again or contact support{competitionReference ? ` with competition ID #${competitionReference}` : ""}.
      </p>}
      {checkButton}
    </>}
    {operation.status === "blocked" && unlockButton && <div className="mt-2">{unlockButton}</div>}
    <Modal isOpen={confirmingUnlock} toggle={() => setConfirmingUnlock(false)}>
      <ModalHeader toggle={() => setConfirmingUnlock(false)}>Unlock draw changes?</ModalHeader>
      <ModalBody>
        <p className="mb-2">{operation.action
          ? `CP stops waiting for ${copy.action}${requested ? `, requested ${requested}` : ""}.`
          : "CP forgets the saved draw operation in this tab."}</p>
        <p className="mb-0">Nothing is repeated or deleted. If the server finishes it later, Action Logs will show it.</p>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={() => setConfirmingUnlock(false)}>Cancel</Button>{" "}
        <Button color="primary" onClick={unlock}>Unlock draw changes</Button>
      </ModalFooter>
    </Modal>
  </div>
}
